import { CartAddEvent } from '@theme/events';

/**
 * Alphabet sticker name builder (snippets/alphabet-builder.liquid).
 *
 * The text input is the single source of truth: tapping a key appends to it,
 * typing is filtered to characters the product has a variant for. On add, the
 * name is grouped into one line per character ("NABEEL" → E×2) and posted as a
 * single /cart/add.js call, so the add is all-or-nothing. Each line carries a
 * hidden `_name` property; lines from different names stay separate.
 *
 * The cart event uses source 'product-form-component' like the theme's own
 * product form, so the cart drawer / post-add kit modal react as usual.
 */
class AlphabetBuilder extends HTMLElement {
  /** @type {Record<string, {id: number, price: number, available: boolean, image: string | null}>} */
  #variants = {};
  /** @type {HTMLInputElement | null} */
  #input = null;
  #busy = false;

  connectedCallback() {
    if (this.#input) return;

    try {
      this.#variants = JSON.parse(this.querySelector('[data-abc-variants]')?.textContent ?? '{}');
    } catch (error) {
      console.error('alphabet-builder: bad variant data', error);
    }

    this.#input = this.querySelector('[data-abc-input]');

    for (const key of this.querySelectorAll('[data-abc-key]')) {
      const char = /** @type {HTMLElement} */ (key).dataset.abcKey ?? '';
      const variant = this.#variants[char];
      if (!variant) key.setAttribute('hidden', '');
      else if (!variant.available) {
        key.setAttribute('disabled', '');
        key.setAttribute('aria-label', `${char} — sold out`);
      }
    }
    const extras = this.querySelector('[data-abc-extras]');
    if (extras && !extras.querySelector('[data-abc-key]:not([hidden])')) extras.setAttribute('hidden', '');

    this.addEventListener('click', this.#onClick);
    this.#input?.addEventListener('input', this.#onInput);
    this.#input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#add();
      }
    });

    this.#render();
  }

  get #min() {
    return parseInt(this.dataset.min ?? '2', 10) || 2;
  }

  get #max() {
    return parseInt(this.dataset.max ?? '20', 10) || 20;
  }

  /** Characters of the current value that have a variant, in order. */
  get #chars() {
    return [...(this.#input?.value ?? '')].filter((char) => char in this.#variants);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const key = target.closest('[data-abc-key]');
    if (key instanceof HTMLButtonElement && !key.disabled && this.#input) {
      if (this.#chars.length >= this.#max) return;
      this.#input.value += key.dataset.abcKey ?? '';
      this.#render();
      return;
    }

    if (target.closest('[data-abc-backspace]') && this.#input) {
      this.#input.value = [...this.#input.value].slice(0, -1).join('');
      this.#render();
      return;
    }

    if (target.closest('[data-abc-add]')) this.#add();
  };

  #onInput = () => {
    if (!this.#input) return;
    // Uppercase, drop spaces and anything without a variant, cap the length.
    const cleaned = [...this.#input.value.toUpperCase()]
      .filter((char) => char in this.#variants)
      .slice(0, this.#max)
      .join('');
    if (cleaned !== this.#input.value) this.#input.value = cleaned;
    this.#render();
  };

  #render() {
    const chars = this.#chars;
    const soldOut = [...new Set(chars.filter((char) => !this.#variants[char]?.available))];

    const preview = this.querySelector('[data-abc-preview]');
    if (preview) {
      preview.querySelectorAll('.abc__sticker').forEach((el) => el.remove());
      const empty = preview.querySelector('[data-abc-preview-empty]');
      if (empty instanceof HTMLElement) empty.hidden = chars.length > 0;

      for (const char of chars) {
        const variant = this.#variants[char];
        let el;
        if (variant?.image) {
          el = document.createElement('img');
          el.src = variant.image;
          el.alt = char;
          el.loading = 'lazy';
        } else {
          el = document.createElement('span');
          el.textContent = char;
          el.classList.add('abc__sticker--text');
        }
        el.classList.add('abc__sticker');
        if (!variant?.available) el.classList.add('abc__sticker--soldout');
        preview.append(el);
      }
    }

    const count = this.querySelector('[data-abc-count]');
    if (count) count.textContent = `${chars.length} sticker${chars.length === 1 ? '' : 's'}`;

    const total = this.querySelector('[data-abc-total]');
    if (total) {
      const cents = chars.reduce((sum, char) => sum + (this.#variants[char]?.price ?? 0), 0);
      total.textContent = chars.length ? this.#formatMoney(cents) : '';
    }

    if (soldOut.length) {
      this.#showError(`Sorry, ${soldOut.join(', ')} ${soldOut.length === 1 ? 'is' : 'are'} sold out right now.`);
    } else {
      this.#hideError();
    }

    const add = this.querySelector('[data-abc-add]');
    if (add instanceof HTMLButtonElement) {
      add.disabled = this.#busy || chars.length < this.#min || soldOut.length > 0;
    }
  }

  async #add() {
    const chars = this.#chars;
    if (this.#busy || chars.length < this.#min) return;
    if (chars.some((char) => !this.#variants[char]?.available)) return;

    const name = chars.join('');
    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const char of chars) counts.set(char, (counts.get(char) ?? 0) + 1);

    const items = [...counts].map(([char, quantity]) => ({
      id: this.#variants[char].id,
      quantity,
      properties: { _name: name },
    }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    this.#busy = true;
    this.#render();

    // A stalled connection must not leave the button disabled forever.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
        signal: controller.signal,
      }).then((r) => r.json());

      if (response.status) {
        this.#showError(response.description || response.message || 'Could not add to cart.');
        return;
      }

      if (this.#input) this.#input.value = '';

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'alphabet-builder', {
          source: 'product-form-component',
          itemCount: chars.length,
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );
      this.dispatchEvent(new CustomEvent('alphabet-builder:added', { bubbles: true, detail: { name } }));
    } catch (error) {
      console.error('alphabet-builder: add failed', error);
      this.#showError('Could not reach the cart. Check your connection and try again.');
    } finally {
      clearTimeout(timer);
      this.#busy = false;
      // Re-render without wiping a just-shown cart error.
      const error = this.querySelector('[data-abc-error]');
      const message = error instanceof HTMLElement && !error.hidden ? error.textContent : null;
      this.#render();
      if (message) this.#showError(message);
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const currency = this.dataset.currency || 'INR';
    try {
      return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-abc-error]');
    if (error instanceof HTMLElement) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  #hideError() {
    const error = this.querySelector('[data-abc-error]');
    if (error instanceof HTMLElement) error.hidden = true;
  }
}

if (!customElements.get('alphabet-builder')) customElements.define('alphabet-builder', AlphabetBuilder);
